<template>
    <div class="pt-2 px-4">
        <top-area :reservation="reservation"></top-area>

        <person-ticket-selector v-if="showTicketPicker" :reservation="reservation" :readOnly="getIsAddingReservation"></person-ticket-selector>

         <!-- Rules PAX Book Message -->
        <transition name="fade">
            <AlertMessage v-if="rulesPaxBookMessage" :message="rulesPaxBookMessage" icon="ico-alert" bold />
        </transition>
    
        <!-- Rules Pending Event Message -->
        <transition name="fade">
            <AlertMessage v-if="rulesPendingEventMessage" :message="rulesPendingEventMessage" icon="ico-alert" bold />
        </transition>
        
        <!-- Gender Prices Dependencies -->
        <transition name="fade" v-for="(message, index) in genderPricesDependenciesMessage" :key="index" appear>
            <AlertMessage v-if="message" :message="message" icon="ico-alert" bold />
        </transition>

        <!-- Cutoff Messaga -->
        <div v-if="cutoffMessage" class="text-center color-6 radius-normal border-color-6 p-2 bg-lightest my-2">
            {{cutoffMessage}}
        </div>

        <div class="bg-lightest radius-normal border-light my-2">
            <div class="col-sm-12 pt-3">
                <h5 class="w-100">
                    Customer Details 
                    <i v-if="isCustomerDetailsDataInStorage" v-tooltip="'Fill in last customer details'" @click="fillWithLastCustomerDetails()" class="fas fa-clipboard ml-2 bg-lighter  dark text-centerpointer pointer"></i>
                    <i v-else v-tooltip="'No customer details found in memory!'" @click="$toasted.error('No customer details found in memory!')" class="fas fa-clipboard ml-2 bg-lighter  dark text-centerpointer not-allowed"></i>
                </h5>
                <customer-details :reservation="reservation"></customer-details>
            </div>
        </div>

        <div v-if="showBillinDetailsForm" class="bg-lightest radius-normal border-light mt-2 mb-2">
            <div class="col-sm-12 pt-3">
                <h5 class="w-100">Billing Details</h5>
                <billing-details :reservation="reservation"></billing-details>
            </div>
        </div>
    </div>
</template>

<script>
import topArea from './Details/TopArea'
import { mapGetters } from 'vuex'
import PersonTicketSelector from './Details/PersonTicketSelector'
import CustomerDetails from './Details/CustomerDetails'
import BillingDetails from './Details/BillingDetails'
import AlertMessage from '@/tools/alertMessage/AlertMessage'
import { reservationsMixin } from '@/mixins/reservations'

export default {
    mixins: [
        reservationsMixin
    ],
    props: {
        reservation: {
            type: Object
        },
    },
    computed: {
        ...mapGetters([
            'account',
            'getIsAddingReservation'
        ]),
        isCustomerDetailsDataInStorage() {
            const customerDetails = localStorage.getItem('last-customer-details');
            const now = new Date();

            if (customerDetails && now.getTime() > customerDetails.expiry) {
                return false;
            }

            return customerDetails !== null;
        },
        event() {
            return this.reservation.event;
        },
        showTicketPicker() {
            // Dont show it is "Per Order" without participants - meaning that is just one ticket per order
            if (this.reservation.bookingGenderPrices && this.reservation.bookingGenderPrices.length == 1 && this.reservation.bookingGenderPrices[0].genderPriceType == 1 ) {
                return false;
            } else {
                return true;
            }
        },
        showBillinDetailsForm() {
            return this.selectedSellerAccessList ? this.selectedSellerAccessList.channel.isInvoiceAutomatic : false;
        },
        cutoffMessage() {
            if (typeof this.event.hasCutOff == "undefined" || this.event.hasCutOff != true) {
                return null;
            } else {
                // Are we inside the cutoff time window? AND the cutoff aplies to "my team"
                if (moment().isAfter(moment(this.event.eventCutOffStart)) && this.event.cutOffChannelsMain.some(channel => channel.channelId == this.account.salesChannel.id)) {

                    // Cutoff is enabled, is it softCutOff?
                    if (this.event.softCutOff) {
                        return 'This event is in Soft Cutoff, this reservation will be created and set as Pending Confirmation.';
                    } else {
                        // Is hard cutoff
                        return null;
                    }
                } else {
                    return null;
                }
            }
        },
        totalPax() {
            return this.reservation.bookingGenderPrices.reduce((acc, comp) => {
                return acc += comp.quantityToBook;
            }, 0);
        },
        rulesPaxBookMessage() {
            if (this.isSomeRulesPaxPerBooking) {
                if (this.event.rejectBookingBasedOnMinMax) {
                    if (this.totalPax < this.reservation.event.minimumPaxPerBooking) {
                        return `A minimum of ${this.reservation.event.minimumPaxPerBooking} PAX per booking is required.`;
                    } else if (this.reservation.event.maximumPaxPerBooking != 0 && this.totalPax > this.reservation.event.maximumPaxPerBooking) {
                        return `A maximum of ${this.reservation.event.maximumPaxPerBooking} PAX per booking is required.`;
                    }
                } else {
                    if (this.totalPax < this.reservation.event.minimumPaxPerBooking) {
                        return `A minimum of ${this.reservation.event.minimumPaxPerBooking}  PAX per booking is required, otherwise the booking remains pending confirmation.`;
                    } else if (this.reservation.event.maximumPaxPerBooking != 0 && this.totalPax > this.reservation.event.maximumPaxPerBooking) {
                        return  `A maximum of ${this.reservation.event.maximumPaxPerBooking} PAX per booking is required, otherwise the booking remains pending confirmation.`;
                    }
                } 
            } else {
                return null
            };
        },
        isSomeRulesPaxPerBooking() {
            if (this.reservation.event.minimumPaxPerBooking > 0 || this.reservation.event.maximumPaxPerBooking > 0) {
                return true;
            } else {
                return false;
            };
        },
        rulesPendingEventMessage() {
            if (this.reservation.event.minimumLotation) {
                if ((this.totalPax + this.reservation.event.quantityBooked) < this.reservation.event.minimumLotation) {
                    return `A minimum of ${this.reservation.event.minimumLotation} PAX per event is required, otherwise the booking remains pending confirmation.`;
                } 
            } else {
                return null;
            }
        },
        genderPricesDependenciesMessage() {
            const validationMessages = [];

            if (!this.reservation.bookingGenderPrices) {
                return [];
            }

            this.reservation.bookingGenderPrices.forEach(price => {
                if (price.quantityToBook <= 0) {
                    return;
                }

                if (!price.genderPriceDependencies || price.genderPriceDependencies.length === 0) {
                    return;
                }

                const dependencyType = price.dependencySettings && price.dependencySettings.dependencyType;
                const deps = price.genderPriceDependencies;

                // Dependency Type | 1 - OR | 0 - AND
                if (dependencyType === 1) {
                    const hasSomeDependentPrice = deps.some(dep => {
                        const dependentPrice = this.reservation.bookingGenderPrices.find(bgp =>
                            bgp.audienceType === dep.genderPriceDependsOn.audienceType &&
                            bgp.audienceTypeGroup === dep.genderPriceDependsOn.audienceTypeGroup
                        );

                        return dependentPrice && dependentPrice.quantityToBook >= dep.quantity;
                    });

                    if (!hasSomeDependentPrice) {
                        const allDepsJoined = this.buildDepsList(deps, ' OR ');
                        const errorMessage = `The ${price.audienceTypeDescription} requires at least ${allDepsJoined} in the reservation.`;
                        validationMessages.push(errorMessage);
                    }
                } else {
                    // Filters unmet dependencies
                    const unmetDeps = deps.filter(dep => {
                        const dependentPrice = this.reservation.bookingGenderPrices.find(bgp =>
                            bgp.audienceType === dep.genderPriceDependsOn.audienceType &&
                            bgp.audienceTypeGroup === dep.genderPriceDependsOn.audienceTypeGroup
                        );
                        
                        return !dependentPrice || dependentPrice.quantityToBook < dep.quantity;
                    });

                    if (unmetDeps.length > 0) {
                        const allDepsJoined = this.buildDepsList(unmetDeps, ' AND ');
                        const errorMessage = `The ${price.audienceTypeDescription} requires at least ${allDepsJoined} in the reservation.`;

                        validationMessages.push(errorMessage);
                    }
                }
            });

            // Emits event for payment btns indicating if there are unfulfilled dependencies
            if (validationMessages.length === 0 || this.account.accessType === 1) {
                this.eventHub.$emit("genderPriceDeps", false);
            } else {
                this.eventHub.$emit("genderPriceDeps", true);
            }

            return validationMessages;
        },
    },
    methods: {
        // Find the description corresponding to audienceType
        getAudienceTypeDescription(audienceType, audienceTypeGroup) {
            const price = this.reservation.bookingGenderPrices.find(p => p.audienceType === audienceType && p.audienceTypeGroup === audienceTypeGroup);

            return price ? price.audienceTypeDescription : "Unknown Group";
        },
        buildDepsList(deps, operator) {
            if (!Array.isArray(deps)) {
                return [];
            }

            // For each dependency, generate only the part: '1 ADULT' and joined them
            return deps.map(dep => {
                const dependentDescription = this.getAudienceTypeDescription(
                    dep.genderPriceDependsOn.audienceType,
                    dep.genderPriceDependsOn.audienceTypeGroup
                );

                return `${dep.quantity} ${dependentDescription}`;
            }).join(operator);
        }
    },
    components: {
        topArea,
        PersonTicketSelector,
        CustomerDetails,
        BillingDetails,
        AlertMessage
    }
}
</script>

<style lang="scss" scoped>
    .not-allowed {
        cursor: not-allowed !important;
        color: #cdd9e4 !important;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.5s ease, transform 0.5s ease;
    }

    .fade-enter,
    .fade-leave-to {
        opacity: 0;
        transform: translateY(20px);
    }
</style>



// WEBPACK FOOTER //
// Details.vue?548edc90